import React, { useEffect, useState } from 'react';
import KpDropzone, { formateraStorlek } from '../ui/dropzone/KpDropzone';
import { ACCEPT, MAX_FIL_BYTES, MAX_FILER } from './forslagRegler';

/**
 * Filer i förslagsformuläret (BL-169): dra och släpp, välj, klistra in (hanteras av
 * ForslagPanel) och skärmdump av vyn bakom panelen. Själva uppladdningen sker efter skick.
 *
 * Props:
 *  - filer: File[]
 *  - onLaggTill(files: File[])   anroparen sorterar bort för många/för stora/fel typ
 *  - onTaBort(index)
 *  - fel: string[]               avvisade filer från senaste tillägg
 *  - visaSkarmdump?: boolean
 */
function Miniatyr({ fil }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!fil.type || !fil.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return undefined;
    const u = URL.createObjectURL(fil);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [fil]);
  if (!url) {
    return (
      <span className="forslag-fil__ikon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
        </svg>
      </span>
    );
  }
  return <img className="forslag-fil__bild" src={url} alt="" />;
}

async function taSkarmdump() {
  const dolda = Array.from(document.querySelectorAll('.kp-panel, .kp-panel-backdrop, .fb-fab'));
  dolda.forEach((el) => { el.style.visibility = 'hidden'; });
  try {
    const { default: h2c } = await import('html2canvas');
    const canvas = await h2c(document.body, { useCORS: true, logging: false });
    return await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob ? new File([blob], `skarmdump-${Date.now()}.png`, { type: 'image/png' }) : null), 'image/png');
    });
  } catch {
    return null;
  } finally {
    dolda.forEach((el) => { el.style.visibility = ''; });
  }
}

export default function ForslagFiler({ filer, onLaggTill, onTaBort, fel = [], visaSkarmdump = true }) {
  const [tar, setTar] = useState(false);
  const fullt = filer.length >= MAX_FILER;

  const skarmdump = async () => {
    setTar(true);
    const fil = await taSkarmdump();
    setTar(false);
    if (fil) onLaggTill([fil]);
  };

  return (
    <div className="forslag-filer">
      <KpDropzone
        onFiles={onLaggTill}
        accept={ACCEPT}
        maxBytes={MAX_FIL_BYTES}
        disabled={fullt}
        title={fullt ? `Högst ${MAX_FILER} filer` : 'Dra hit filer, klistra in eller välj fil'}
        help="Bilder, PDF, Office, text och zip · högst 25 MB per fil"
      />
      {fel.length > 0 && (
        <ul className="forslag-filer__fel" role="alert">
          {fel.map((t) => <li key={t}>{t}</li>)}
        </ul>
      )}
      {filer.length > 0 && (
        <ul className="forslag-fillista" aria-label="Valda filer">
          {filer.map((f, i) => (
            <li className="forslag-fil" key={`${f.name}-${f.size}-${i}`}>
              <Miniatyr fil={f} />
              <span className="forslag-fil__namn" title={f.name}>{f.name}</span>
              <span className="forslag-fil__storlek">{formateraStorlek(f.size)}</span>
              <button type="button" className="kp-icon-btn forslag-fil__bort" onClick={() => onTaBort(i)} aria-label={`Ta bort ${f.name}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}
      {visaSkarmdump && (
        <button type="button" className="forslag-btn forslag-btn--sm forslag-btn--sek forslag-skarmdump" onClick={skarmdump} disabled={tar || fullt}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
          </svg>
          {tar ? 'Tar skärmdump...' : 'Ta skärmdump av vyn'}
        </button>
      )}
    </div>
  );
}
